import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
} from '@hub-la/shadcn'
import { Download } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDownloadCouponsXlsx } from '../hooks/use-download-coupons-xlsx'
import { Field, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

type Props = {
  filters: any
  offers: any[]
}

export const ConfirmExportModal: React.FC<Props> = ({ filters, offers }) => {
  const { t } = useTranslation()
  const [showConfirmModal, setShowConfirmModal] = React.useState(false)

  const form = useFormik<{
    receiverEmail: string
  }>({
    initialValues: {
      receiverEmail: '',
    },
    enableReinitialize: true,
    onSubmit: async () => {
      await exportXlsx()
        .then(() => {
          handleClose()
        })
        .catch(console.error)
    },
    validationSchema: Yup.object().shape({
      receiverEmail: Yup.string().email('Email inválido').required('Email é obrigatório'),
    }),
  })

  const {
    mutateAsync: exportXlsx,
    isLoading: loading,
    isSuccess,
    isError,
    reset,
  } = useDownloadCouponsXlsx({
    ...filters,
    offers,
    receiverEmail: form.values.receiverEmail,
  })

  function handleClose() {
    reset()
    form.resetForm()
    setShowConfirmModal(false)
  }

  function getModalMessage() {
    if (isSuccess) return t('exportXlsx.successMessage')
    return t('exportXlsx.generalErrorMessage')
  }

  if (isSuccess || isError) {
    return (
      <Dialog open={showConfirmModal} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('exportXlsx.title')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>{getModalMessage()}</p>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleClose} variant="outline">
              {t('exportXlsx.getIt')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={showConfirmModal}
      onOpenChange={() => {
        setShowConfirmModal((prevState) => !prevState)
        if (showConfirmModal) {
          handleClose()
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          {t('exportXlsx.confirm')}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full md:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('exportXlsx.title')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-2">
          {filters.search ? (
            <div className="grid grid-cols-3 items-center gap-4">
              <Label>Busca</Label>
              <div className="col-span-2 h-auto p-2 border rounded-lg items-center text-sm background text-muted-foreground">
                {filters.search}
              </div>
            </div>
          ) : null}
          {filters.offerIds?.length > 0 ? (
            <div className="grid grid-cols-3 items-center gap-4">
              <Label>Produtos</Label>
              <div className="col-span-2 h-auto p-2 border rounded-lg items-center text-sm background text-muted-foreground">
                {filters.offerIds.map((id) => offers.find((o) => o.id === id)?.name).join(', ')}
              </div>
            </div>
          ) : null}
          {filters.statuses?.length > 0 ? (
            <div className="grid grid-cols-3 items-center gap-4">
              <Label>Status</Label>
              <div className="col-span-2 h-auto p-2 border rounded-lg items-center text-sm background text-muted-foreground">
                {filters.statuses.map((status) => t(`status.${status}`)).join(', ')}
              </div>
            </div>
          ) : null}
        </div>
        <FormikProvider value={form}>
          <form onSubmit={form.handleSubmit}>
            <div className="grid gap-2 my-2">
              <Label htmlFor="receiverEmail">{t('exportXlsx.email')}</Label>
              <Field
                as={Input}
                id="receiverEmail"
                name="receiverEmail"
                placeholder="example@email.com"
                type="email"
                className={
                  form.errors.receiverEmail && form.touched.receiverEmail && form.dirty ? 'border-red-500' : ''
                }
              />
              {form.errors.receiverEmail && form.touched.receiverEmail && form.dirty ? (
                <div className="text-sm text-red-500 gap-2">{form.errors.receiverEmail}</div>
              ) : null}
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <DialogClose>
                <Button variant="outline" disabled={loading} onClick={handleClose}>
                  {t('exportXlsx.cancel')}
                </Button>
              </DialogClose>
              <Button
                type={'submit'}
                loading={loading}
                className="flex gap-2"
                disabled={!!form.errors.receiverEmail || !form.values.receiverEmail}
              >
                <Download className="h-4 w-4" />
                {t('exportXlsx.confirm')}
              </Button>
            </div>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}
